import React, { useEffect, useState } from "react";
import { getApiUrl } from "../../utils/api";
import "./ContactMessages.css";

function ContactMessages() {
  const [contacts, setContacts] = useState([]);
  const [filteredContacts, setFilteredContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("All");

  // Delete modal state
  const [deleteModal, setDeleteModal] = useState({
    isOpen: false,
    isBulk: false,
    item: null
  });
  const [isDeleting, setIsDeleting] = useState(false);

  const fetchContacts = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("adminToken");
      const res = await fetch(getApiUrl("/admin/contacts"), {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      const data = await res.json();
      if (data.success && data.data) {
        setContacts(data.data);
        setFilteredContacts(data.data);
      }
    } catch (err) {
      console.error("Error fetching contact messages:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchContacts();
  }, []);

  useEffect(() => {
    let result = [...contacts];

    if (statusFilter !== "All") {
      result = result.filter((c) => c.status === statusFilter);
    }

    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      result = result.filter(
        (c) =>
          `${c.firstName} ${c.lastName}`.toLowerCase().includes(q) ||
          (c.email && c.email.toLowerCase().includes(q)) ||
          (c.trackingId && c.trackingId.toLowerCase().includes(q))
      );
    }

    setFilteredContacts(result);
  }, [searchQuery, statusFilter, contacts]);

  const getStatusBadge = (status) => {
    switch (status) {
      case "In Progress":
        return <span className="status-badge badge-progress">In Progress</span>;
      case "Resolved":
        return <span className="status-badge badge-resolved">Resolved</span>;
      case "Closed":
        return <span className="status-badge badge-closed">Closed</span>;
      default:
        return <span className="status-badge badge-received">Received</span>;
    }
  };

  const handleViewDetails = (trackingId) => {
    window.open(`/contact-messages/${trackingId}`, "_blank");
  };

  const openDeleteSingleModal = (contact) => {
    setDeleteModal({
      isOpen: true,
      isBulk: false,
      item: contact
    });
  };

  const openDeleteAllModal = () => {
    setDeleteModal({
      isOpen: true,
      isBulk: true,
      item: null
    });
  };

  const closeDeleteModal = () => {
    if (isDeleting) return;
    setDeleteModal({
      isOpen: false,
      isBulk: false,
      item: null
    });
  };

  const handleConfirmDelete = async () => {
    setIsDeleting(true);
    try {
      const token = localStorage.getItem("adminToken");
      let url = "";

      if (deleteModal.isBulk) {
        url = getApiUrl("/admin/contacts");
      } else {
        const idToDelete = deleteModal.item?.trackingId || deleteModal.item?._id;
        url = getApiUrl(`/admin/contacts/${encodeURIComponent(idToDelete)}`);
      }

      const res = await fetch(url, {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      const data = await res.json();
      if (data.success) {
        setDeleteModal({ isOpen: false, isBulk: false, item: null });
        await fetchContacts();
      } else {
        alert(data.message || "Failed to delete contact message");
      }
    } catch (err) {
      console.error("Error deleting contact:", err);
      alert("Error occurred while deleting. Please try again.");
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <div className="contact-messages-container">
      <div className="page-header-row">
        <div>
          <h2>📩 Contact Messages & Tickets</h2>
          <p className="page-sub">Manage user inquiries and update ticket resolution status</p>
        </div>

        <div className="header-actions">
          <button className="btn-refresh" onClick={fetchContacts} disabled={loading}>
            🔄 Refresh
          </button>
          <button
            className="btn-delete-all"
            onClick={openDeleteAllModal}
            disabled={loading || contacts.length === 0}
            title="Delete all contact messages"
          >
            🗑️ Delete All
          </button>
        </div>
      </div>

      {/* FILTER & SEARCH BAR */}
      <div className="table-controls-row">
        <div className="search-input-wrapper">
          <span className="search-icon">🔍</span>
          <input
            type="text"
            placeholder="Search by name, email, tracking ID..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>

        <div className="filter-pill-group">
          {["All", "Received", "In Progress", "Resolved", "Closed"].map((st) => (
            <button
              key={st}
              className={`filter-pill ${statusFilter === st ? "active" : ""}`}
              onClick={() => setStatusFilter(st)}
            >
              {st}
            </button>
          ))}
        </div>
      </div>

      {/* MESSAGES TABLE */}
      <div className="dash-glass-panel full-width-panel">
        {loading ? (
          <div className="loading-spinner-box">
            <div className="spinner"></div>
            <p>Loading contact messages...</p>
          </div>
        ) : filteredContacts.length > 0 ? (
          <div className="table-responsive">
            <table className="glass-table">
              <thead>
                <tr>
                  <th>S.No</th>
                  <th>Date & Time</th>
                  <th>User Name</th>
                  <th>Ticket ID</th>
                  <th>Status</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {filteredContacts.map((contact, idx) => (
                  <tr key={contact._id || idx}>
                    <td>{idx + 1}</td>
                    <td>{new Date(contact.createdAt).toLocaleString()}</td>
                    <td className="user-name-cell">
                      {contact.firstName} {contact.lastName}
                      <span className="email-sub">{contact.email}</span>
                    </td>
                    <td>
                      <code className="ticket-code">{contact.trackingId}</code>
                    </td>
                    <td>{getStatusBadge(contact.status)}</td>
                    <td>
                      <div className="action-buttons-group">
                        <button
                          className="btn-action-view"
                          onClick={() => handleViewDetails(contact.trackingId)}
                        >
                          View Details 👁️
                        </button>
                        <button
                          className="btn-action-delete"
                          onClick={() => openDeleteSingleModal(contact)}
                        >
                          Delete 🗑️
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="empty-state">
            <p>No contact messages match your criteria.</p>
          </div>
        )}
      </div>

      {/* WARNING DELETE MODAL */}
      {deleteModal.isOpen && (
        <div className="delete-modal-overlay" onClick={closeDeleteModal}>
          <div className="delete-modal-card" onClick={(e) => e.stopPropagation()}>
            <div className="warning-icon-badge">
              <svg width="34" height="34" viewBox="0 0 24 24" fill="none" stroke="#ef4444" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                <path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3Z" />
                <line x1="12" y1="9" x2="12" y2="13" />
                <line x1="12" y1="17" x2="12.01" y2="17" />
              </svg>
            </div>

            <h3 className="delete-modal-title">Are you sure want to Delete</h3>

            <p className="delete-modal-text">
              {deleteModal.isBulk
                ? "Are you sure you want to delete all contact messages? This action cannot be undone."
                : `Are you sure you want to delete contact message from "${deleteModal.item?.firstName} ${deleteModal.item?.lastName}" (${deleteModal.item?.trackingId})?`}
            </p>

            <div className="delete-modal-actions">
              <button
                className="btn-modal-cancel"
                onClick={closeDeleteModal}
                disabled={isDeleting}
              >
                Cancel
              </button>
              <button
                className="btn-modal-delete"
                onClick={handleConfirmDelete}
                disabled={isDeleting}
              >
                {isDeleting ? "Deleting..." : "Delete"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ContactMessages;
